import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Mail } from 'lucide-react'
import { Logo } from '@/components/generals/logo'

import {
    CONTACT_EMAIL,
    FIGURE_SOURCES,
    FOOTER_COLUMNS,
    KVK_NUMMER,
    MAILTO_VRAAG,
    external
} from '@/constants/leesritme'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer id='footer' className='border-t border-ink/10 bg-sand/60'>
            <div className='mx-auto max-w-7xl px-5 pt-16 pb-10 sm:px-8'>
                <div className='grid gap-12 lg:grid-cols-[1.1fr_2fr]'>
                    <div className='max-w-sm'>
                        <Link href='/' aria-label='Leesritme, naar de homepage' className='inline-block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf'>
                            <Logo />
                        </Link>

                        <p className='mt-5 font-heading text-xl leading-snug font-medium text-ink'>
                            Meer lezen. Elke dag. <span className='italic text-leaf'>Zonder extra werk.</span>
                        </p>

                        <p className='mt-4 text-[0.9375rem] leading-relaxed text-muted-foreground'>
                            Leesritme wordt gemaakt door Roman Storck, leerkracht met ervaring in het speciaal basisonderwijs.
                        </p>

                        <a
                            href={MAILTO_VRAAG}
                            className='mt-5 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/70 px-4 py-2 text-sm font-semibold text-ink transition hover:bg-white'
                        >
                            <Mail className='size-4 text-leaf' aria-hidden='true' />
                            {CONTACT_EMAIL}
                        </a>

                        <p className='mt-4 text-sm text-muted-foreground'>
                            KvK {KVK_NUMMER} · <a href={external('/privacy#wie')} className='underline decoration-ink/20 underline-offset-4 hover:text-ink hover:decoration-ink'>Contact en gegevens</a>
                        </p>
                    </div>

                    <div className='grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-4'>
                        {FOOTER_COLUMNS.map(column => (
                            <div key={column.title}>
                                <h2 className='text-xs font-bold tracking-[0.14em] text-ink uppercase'>
                                    {column.title}
                                </h2>

                                <ul className='mt-4 grid gap-2.5'>
                                    {column.links.map(link => (
                                        <li key={link.label}>
                                            <a href={link.href} className='text-[0.9375rem] text-muted-foreground transition hover:text-ink'>
                                                {link.label}
                                            </a>

                                            {link.description && (
                                                <span className='ml-1 text-xs text-muted-foreground/80'>
                                                    ({link.description})
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='mt-14 border-t border-ink/10 pt-6'>
                    <p className='text-xs leading-relaxed text-muted-foreground'>
                        <span className='font-semibold text-ink'>Bronnen bij de cijfers: </span>
                        {FIGURE_SOURCES.map((source, index) => (
                            <span key={source.href}>
                                <a href={source.href} target='_blank' rel='noopener noreferrer' className='underline decoration-ink/20 underline-offset-2 hover:text-ink hover:decoration-ink'>
                                    {source.label}
                                </a>
                                {index < FIGURE_SOURCES.length - 1 ? ' · ' : ''}
                            </span>
                        ))}
                    </p>

                    <p className='mt-3 text-xs text-muted-foreground'>
                        © 2026 Leesritme
                    </p>
                </div>
            </div>
        </footer>
    )
}
